import { clsx } from 'clsx'
import { useIsFetching } from '@tanstack/react-query'
import {
  ChevronDown,
  LayoutGrid,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sun,
  SunMoon,
  X,
} from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { CommandPalette } from './CommandPalette'
import { MOBILE_PRIMARY, useNavGroups, type NavGroup, type NavItem } from './nav'
import { ProjectSwitcher, ShellBanners, UserMenu } from './ShellParts'
import { useLocalStorage, useMediaQuery } from '@/hooks/useLocalStorage'
import { useTheme } from '@/hooks/useTheme'
import { IconButton, Spinner } from '@/components/ui/primitives'
import { RouteErrorBoundary } from '@/components/ui/feedback'

function Logo({ compact }: { compact?: boolean }) {
  return (
    <NavLink to="/" className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden
        className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent text-accent-ink shadow-soft"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
          <rect x="3" y="12" width="4" height="9" rx="1" />
          <rect x="10" y="7" width="4" height="14" rx="1" />
          <rect x="17" y="3" width="4" height="18" rx="1" />
        </svg>
      </span>
      {!compact ? (
        <span className="min-w-0">
          <span className="block truncate font-serif text-sm font-semibold leading-tight text-ink">
            Ad Insights Lab
          </span>
          <span className="block truncate text-2xs text-ink-3">広告運用</span>
        </span>
      ) : null}
    </NavLink>
  )
}

function ThemeButton() {
  const { choice, cycle } = useTheme()
  const icon =
    choice === 'light' ? <Sun className="h-4 w-4" /> : choice === 'dark' ? <Moon className="h-4 w-4" /> : <SunMoon className="h-4 w-4" />
  const label =
    choice === 'light' ? 'ライトテーマ（クリックでダーク）' : choice === 'dark' ? 'ダークテーマ（クリックでOS追従）' : 'OS追従（クリックでライト）'
  return <IconButton label={label} icon={icon} onClick={cycle} />
}

function NavRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
          isActive
            ? 'bg-accent/10 font-semibold text-accent'
            : 'font-medium text-ink-2 hover:bg-surface-hover hover:text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={clsx('h-4 w-4 shrink-0', isActive ? 'text-accent' : 'text-ink-3')} aria-hidden />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  )
}

function SidebarGroups({ groups, onNavigate }: { groups: NavGroup[]; onNavigate?: () => void }) {
  const [collapsed, setCollapsed] = useLocalStorage<Record<string, boolean>>('ail.nav.collapsed', {})
  const { pathname } = useLocation()

  return (
    <div className="space-y-1">
      {groups.map((group) => {
        const hasActive = group.items.some((i) => pathname === i.to || (!i.end && pathname.startsWith(`${i.to}/`)))
        const isOpen = collapsed[group.label] === undefined ? true : !collapsed[group.label]
        const open = isOpen || hasActive
        return (
          <div key={group.label}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setCollapsed((c) => ({ ...c, [group.label]: open }))}
              className="flex w-full items-center justify-between rounded px-2.5 py-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-3 transition-colors hover:text-ink-2"
            >
              {group.label}
              <ChevronDown className={clsx('h-3 w-3 transition-transform', open && 'rotate-180')} aria-hidden />
            </button>
            {open ? (
              <div className="mb-1 space-y-0.5">
                {group.items.map((item) => (
                  <NavRow key={item.to} item={item} onNavigate={onNavigate} />
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** Thin progress bar: any in-flight query keeps it alive. */
function FetchIndicator() {
  const fetching = useIsFetching()
  return (
    <div
      aria-hidden
      className={clsx(
        'pointer-events-none fixed inset-x-0 top-0 z-[250] h-0.5 overflow-hidden transition-opacity duration-200',
        fetching > 0 ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div className="h-full w-full origin-left animate-[shimmer_1.2s_linear_infinite] bg-gradient-to-r from-transparent via-accent to-transparent" />
    </div>
  )
}

export function Shell() {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [railCollapsed, setRailCollapsed] = useLocalStorage('ail.nav.rail', false)
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const { pathname } = useLocation()
  const groups = useNavGroups()
  const allItems = groups.flatMap((g) => g.items)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((v) => !v)
      }
      if (e.key === '/' && !mod) {
        const el = document.activeElement
        const typing =
          el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el as HTMLElement)?.isContentEditable
        if (!typing) {
          e.preventDefault()
          setPaletteOpen(true)
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    setDrawerOpen(false)
    setMoreOpen(false)
  }, [pathname])

  const sidebarWidth = railCollapsed ? 'lg:w-[68px]' : 'lg:w-[236px]'
  const mainOffset = railCollapsed ? 'lg:pl-[68px]' : 'lg:pl-[236px]'

  return (
    <div className="min-h-dvh bg-bg">
      <FetchIndicator />

      {/* Desktop sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 hidden shrink-0 flex-col border-r border-line bg-surface lg:flex',
          sidebarWidth,
        )}
      >
        <div className={clsx('flex h-14 items-center border-b border-line', railCollapsed ? 'justify-center px-2' : 'px-3')}>
          <Logo compact={railCollapsed} />
        </div>

        {!railCollapsed ? (
          <div className="px-3 pt-3">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex w-full items-center gap-2 rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-left text-xs text-ink-3 transition-colors hover:border-line-strong hover:text-ink-2"
            >
              <Search className="h-3.5 w-3.5" aria-hidden />
              <span className="flex-1">画面を検索</span>
              <kbd className="rounded border border-line bg-surface px-1 py-0.5 text-[10px]">⌘K</kbd>
            </button>
          </div>
        ) : (
          <div className="flex justify-center pt-3">
            <IconButton label="画面を検索" icon={<Search className="h-4 w-4" />} onClick={() => setPaletteOpen(true)} />
          </div>
        )}

        {!railCollapsed ? (
          <div className="px-3 pt-2">
            <ProjectSwitcher />
          </div>
        ) : null}

        <nav className="min-h-0 flex-1 overflow-y-auto p-3" aria-label="メインナビゲーション">
          {railCollapsed ? (
            <div className="flex flex-col items-center gap-1">
              {allItems.map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    title={item.label}
                    aria-label={item.label}
                    className={({ isActive }) =>
                      clsx(
                        'grid h-9 w-9 place-items-center rounded-lg transition-colors',
                        isActive ? 'bg-accent/10 text-accent' : 'text-ink-3 hover:bg-surface-hover hover:text-ink',
                      )
                    }
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </NavLink>
                )
              })}
            </div>
          ) : (
            <SidebarGroups groups={groups} />
          )}
        </nav>

        {!railCollapsed ? (
          <div className="border-t border-line px-3 py-2">
            <UserMenu />
          </div>
        ) : null}
        <div className={clsx('flex items-center border-t border-line p-2', railCollapsed ? 'justify-center' : 'justify-between px-3')}>
          {!railCollapsed ? <ThemeButton /> : null}
          <IconButton
            label={railCollapsed ? 'サイドバーを開く' : 'サイドバーを畳む'}
            icon={railCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            onClick={() => setRailCollapsed((v) => !v)}
          />
        </div>
      </aside>

      {/* Mobile / tablet header */}
      <header className="sticky top-0 z-40 border-b border-line bg-surface/90 backdrop-blur lg:hidden">
        <div className="flex h-14 items-center justify-between gap-2 px-3">
          <div className="flex min-w-0 items-center gap-1.5">
            <IconButton label="メニューを開く" icon={<Menu className="h-5 w-5" />} onClick={() => setDrawerOpen(true)} />
            <Logo />
          </div>
          <div className="flex items-center gap-0.5">
            <IconButton label="画面を検索" icon={<Search className="h-5 w-5" />} onClick={() => setPaletteOpen(true)} />
            <ThemeButton />
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && !isDesktop ? (
        <div className="fixed inset-0 z-[90] lg:hidden">
          <button type="button" aria-label="閉じる" onClick={() => setDrawerOpen(false)} className="absolute inset-0 bg-black/40" />
          <aside className="absolute inset-y-0 left-0 flex w-[280px] flex-col border-r border-line bg-surface shadow-pop">
            <div className="flex h-14 items-center justify-between border-b border-line px-3">
              <Logo />
              <IconButton label="閉じる" icon={<X className="h-5 w-5" />} onClick={() => setDrawerOpen(false)} />
            </div>
            <nav className="min-h-0 flex-1 overflow-y-auto p-3" aria-label="メインナビゲーション">
              <ProjectSwitcher />
              <SidebarGroups groups={groups} onNavigate={() => setDrawerOpen(false)} />
              <UserMenu />
            </nav>
          </aside>
        </div>
      ) : null}

      {/* Main */}
      <main className={clsx('min-w-0 pb-24 lg:pb-10', mainOffset)}>
        <div className="mx-auto min-w-0 max-w-[1680px] px-3 py-4 sm:px-5 sm:py-6">
          <ShellBanners />
          <RouteErrorBoundary key={pathname}>
            <Suspense
              fallback={
                <div className="flex h-64 items-center justify-center" role="status" aria-label="読み込み中">
                  <Spinner className="h-6 w-6" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </div>
      </main>

      {/* Mobile bottom bar */}
      <nav
        aria-label="主要画面"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 backdrop-blur safe-b lg:hidden"
      >
        <div className="grid grid-cols-6">
          {MOBILE_PRIMARY.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'flex h-14 min-w-0 flex-col items-center justify-center gap-0.5 px-1 transition-colors',
                    isActive ? 'text-accent' : 'text-ink-3',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={clsx('h-5 w-5 transition-transform', isActive && 'scale-110')} aria-hidden />
                    <span className="max-w-full truncate text-[10px] font-medium">{item.short ?? item.label}</span>
                  </>
                )}
              </NavLink>
            )
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex h-14 min-w-0 flex-col items-center justify-center gap-0.5 px-1 text-ink-3"
          >
            <LayoutGrid className="h-5 w-5" aria-hidden />
            <span className="text-[10px] font-medium">すべて</span>
          </button>
        </div>
      </nav>

      {/* "すべて" sheet */}
      {moreOpen ? (
        <div className="fixed inset-0 z-[95] lg:hidden">
          <button type="button" aria-label="閉じる" onClick={() => setMoreOpen(false)} className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-line bg-surface p-4 pb-8 shadow-pop animate-slide-up safe-b">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-serif text-base font-semibold text-ink">すべての画面</h2>
              <IconButton label="閉じる" icon={<X className="h-5 w-5" />} onClick={() => setMoreOpen(false)} />
            </div>
            <SidebarGroups groups={groups} onNavigate={() => setMoreOpen(false)} />
          </div>
        </div>
      ) : null}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={allItems} />
    </div>
  )
}
